import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, Download, Plus, List, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { ReportTypeBadge, EmailSentBadge } from "@/components/Badges";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { SendReportDialog } from "@/components/report/SendReportDialog";
import { downloadReportPdf } from "@/lib/pdf";

/** Shown after a report is saved. Lets the user preview/download the PDF, send it to the customer, or continue. */
export const ReportSuccessDialog = ({ report, onClose, newPath }) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);
  const [send, setSend] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  if (!report) return null;

  const download = async () => {
    setBusy("download");
    try {
      await downloadReportPdf(report);
    } catch (e) {
      toast.error("Kunne ikkje hente PDF. Prøv igjen frå rapportlista.");
    } finally {
      setBusy("");
    }
  };

  return (
    <>
      <Dialog open={!!report && !preview && !send} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="sm:max-w-md" data-testid="report-success-dialog">
          <DialogHeader className="items-center text-center">
            <div className="status-ok mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full border">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <DialogTitle className="text-center font-heading text-xl font-bold">Rapport lagra</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center justify-center gap-2 text-center">
              <span className="font-semibold text-foreground" data-testid="report-success-number">{report.report_number}</span>
              <ReportTypeBadge type={report.report_type} />
              <EmailSentBadge count={sentCount} />
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="gap-2 pb-[calc(var(--safe-bottom)+16px)] md:pb-0">
            <Button size="lg" className="h-12 md:h-11" onClick={() => setSend(true)} disabled={!!busy} data-testid="report-success-send-email">
              <Send /> {sentCount > 0 ? "Send igjen" : "Send til kunde"}
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button size="lg" variant="outline" className="h-12 md:h-11" onClick={() => setPreview(true)} disabled={!!busy} data-testid="report-success-preview-pdf">
                <Eye /> Førehandsvis
              </Button>
              <Button size="lg" variant="outline" className="h-12 md:h-11" onClick={download} disabled={!!busy} data-testid="report-success-download-pdf">
                {busy === "download" ? <Loader2 className="animate-spin" /> : <Download />} Last ned
              </Button>
            </div>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <Button variant="ghost" className="h-12 md:h-10" onClick={() => { onClose?.(); navigate(newPath || "/rapportar/ny-service"); }} data-testid="report-success-new"><Plus /> Ny rapport</Button>
              <Button variant="ghost" className="h-12 md:h-10" onClick={() => { onClose?.(); navigate("/rapportar"); }} data-testid="report-success-list"><List /> Til rapportar</Button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog open={preview} onOpenChange={(o) => !o && setPreview(false)} report={report} />
      <SendReportDialog report={report} open={send} onOpenChange={(o) => !o && setSend(false)} onSent={() => setSentCount((n) => n + 1)} />
    </>
  );
};
